import { isSafeRedirectPath } from './authSession';
import { readSessionStoredValue, removeSessionStoredValue, writeSessionStoredValue } from './storage';

/**
 * Portal PKCE 인가 흐름의 클라이언트 측 상태.
 *
 * Portal은 로그인 화면을 두지 않고 web(`/auth/portal-authorize`)의 인가 화면으로
 * 복귀 주소·PKCE verifier·state를 넘겨 web 세션을 이어받는다. verifier와 state는
 * 같은 탭의 콜백만 소비할 수 있게 sessionStorage에 두며, 코드·verifier를
 * localStorage에 남기지 않는다.
 */
export const portalOAuthClientId = 'agentteams-portal';

export const portalAuthGrantStorageKey = 'agentteams-portal-auth-grant';

/** 10분이 지난 grant는 서버 코드 만료와 별개로 폐기한다. */
export const portalAuthGrantTtlMs = 10 * 60 * 1000;

export type PortalAuthGrant = {
  state: string;
  verifier: string;
  returnPath: string;
  at: number;
};

export const getPortalWebUrl = () => {
  const configured = process.env.NEXT_PUBLIC_WEB_URL?.trim();
  return (configured && configured.length > 0 ? configured : 'https://agentteams.run').replace(/\/+$/, '');
};

export const getPortalCallbackUrl = (origin: string) => `${origin}/auth/callback`;

const toBase64Url = (bytes: Uint8Array) => {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateUrlSafeToken = (byteLength: number) => {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
};

/** PKCE code_verifier(43~128자). 48바이트 → 64자 base64url. */
export const createPkceVerifier = () => generateUrlSafeToken(48);

export const computeS256Challenge = async (verifier: string) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return toBase64Url(new Uint8Array(digest));
};

/**
 * 미인증 사용자를 web 인가 화면으로 본낸다. 복귀할 Portal 경로는 state 검증과 함께
 * sessionStorage에 두고, 콜백이 같은 탭에서 소비한다.
 */
export const startPortalAuthorize = async (returnPath: string, options?: { prompt?: 'login' }) => {
  const state = generateUrlSafeToken(24);
  const verifier = createPkceVerifier();
  const codeChallenge = await computeS256Challenge(verifier);
  const grant: PortalAuthGrant = {
    state,
    verifier,
    returnPath: isSafeRedirectPath(returnPath) ? returnPath : '/',
    at: Date.now(),
  };
  writeSessionStoredValue(portalAuthGrantStorageKey, JSON.stringify(grant));

  const params = new URLSearchParams({
    client_id: portalOAuthClientId,
    redirect_uri: getPortalCallbackUrl(window.location.origin),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  if (options?.prompt) params.set('prompt', options.prompt);
  window.location.assign(`${getPortalWebUrl()}/auth/portal-authorize?${params.toString()}`);
};

/**
 * 콜백이 기대하는 state와 저장된 grant를 대조한다. 일치하면 grant를 반환한다.
 * verifier 일회성을 지키기 위해 결과와 무관하게 저장소에서 항상 제거한다.
 */
export const consumePortalAuthGrant = (expectedState: string): PortalAuthGrant | null => {
  const raw = readSessionStoredValue(portalAuthGrantStorageKey);
  removeSessionStoredValue(portalAuthGrantStorageKey);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PortalAuthGrant>;
    if (typeof parsed.state !== 'string' || parsed.state !== expectedState) return null;
    if (typeof parsed.verifier !== 'string' || parsed.verifier.length < 43) return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at >= portalAuthGrantTtlMs) return null;
    const returnPath =
      typeof parsed.returnPath === 'string' && isSafeRedirectPath(parsed.returnPath) ? parsed.returnPath : '/';
    return {
      state: parsed.state,
      verifier: parsed.verifier,
      returnPath,
      at: parsed.at,
    };
  } catch {
    return null;
  }
};
